/**
 * H9 / M26 – one error-state pattern for data that failed to load.
 *
 * A failed call must never look like an empty result ("No active POs for this vendor" when the
 * request actually returned 500, or an empty vendor dropdown when it returned 403). Callers keep
 * the failure in state and render the message from here instead of an empty list.
 *
 * Messages are generic on purpose: the server already logs the detail (and strips it from the
 * response), so nothing from the error body is shown.
 */

export type LoadErrorKind = 'forbidden' | 'error';

export interface LoadError {
  kind: LoadErrorKind;
  message: string;
}

function statusOf(error: unknown): number | undefined {
  const e = error as any;
  return e?.response?.status ?? e?.status ?? e?.statusCode;
}

/**
 * @param label plural noun for what was being loaded, e.g. "purchase orders", "vendors".
 */
export function describeLoadError(error: unknown, label: string): LoadError {
  const status = statusOf(error);
  if (status === 401 || status === 403) {
    return { kind: 'forbidden', message: `You don't have access to ${label} – contact your administrator.` };
  }
  return { kind: 'error', message: `Could not load ${label} – try again or contact support.` };
}

/**
 * The message to show for a failed mutation (save, submit, approve). A 4xx body's `error` is a
 * user-facing message written by the API (validation, conflict), so it is shown as-is; anything
 * else gets the generic text.
 */
export function describeActionError(error: unknown, fallback = 'Something went wrong – try again or contact support.'): string {
  const status = statusOf(error);
  const body = (error as any)?.response?.data;
  if (status === 403) return "You don't have permission to do that – contact your administrator.";
  if (status && status >= 400 && status < 500 && typeof body?.error === 'string' && body.error.trim()) {
    return body.error;
  }
  return fallback;
}

/**
 * V3-015 – the message for a failed decision (Approve, Send for Revisions, Decline, Activate,
 * Approve Rate …) on a card or in a decision dialog. A 5xx, a network failure or anything else
 * unexpected gets a friendly line in the verb's own words – *Couldn't approve – try again.* – and
 * never the server's raw text. A 4xx carries a message written for the user (own submission,
 * conflict, validation) and is shown as-is.
 *
 * @param verb lower-case verb phrase, e.g. "approve", "send for revisions", "activate the vendor".
 */
export function describeDecisionError(error: unknown, verb: string): string {
  const friendly = `Couldn't ${verb} – try again.`;
  const status = statusOf(error);
  if (status === undefined || status < 400 || status >= 500) return friendly;
  const body = (error as any)?.response?.data;
  const fromBody = [body?.message, body?.error].find((v) => typeof v === 'string' && v.trim());
  if (fromBody) return fromBody;
  // An axios error's own message is "Request failed with status code …" – never shown.
  if ((error as any)?.response) return friendly;
  const own = (error as any)?.message;
  return typeof own === 'string' && own.trim() ? own : friendly;
}

/** An Error carrying the HTTP status, for fetch-based services, so callers can tell 4xx from 5xx. */
export function httpError(message: string, status: number): Error & { status: number } {
  return Object.assign(new Error(message), { status });
}
